'use client';

import React, { useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginFormProps {
  onSwitchToRegister: () => void;
}

export default function LoginForm({ onSwitchToRegister }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signIn, isAdmin } = useAuth();
  const router = useRouter();
  const supabase = useMemo(() => createClient(), []);

  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>({
    defaultValues: { rememberMe: false },
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await signIn(data.email, data.password);
      toast.success('Welcome back to FarmTradeDirect!');

      // Signing in used to only call router.refresh(), which left you sat on the
      // login page — it looked as though nothing had happened. Send admins to the
      // review queue and everyone else to their dashboard.
      let destination = '/my-dashboard';
      const userId = result?.user?.id;
      if (userId) {
        const { data: profileRow } = await supabase
          .from('profiles')
          .select('is_admin')
          .eq('id', userId)
          .maybeSingle();
        if (profileRow?.is_admin) destination = '/admin/listings';
      }
      router.replace(destination);
    } catch (err: any) {
      const msg = err?.message || 'Sign in failed';
      if (msg.toLowerCase().includes('email not confirmed')) {
        toast.error('Please verify your email address before signing in. Check your inbox.');
      } else if (msg.toLowerCase().includes('invalid login credentials')) {
        toast.error('Incorrect email or password. Please try again.');
      } else {
        toast.error(msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-extrabold text-foreground mb-1">Welcome back</h2>
      <p className="text-muted-foreground text-sm mb-6">Sign in to your FarmTradeDirect account</p>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <div>
          <label className="form-label">Email address</label>
          <input
            type="email"
            className={`form-input ${errors.email ? 'error' : ''}`}
            placeholder="you@yourfarm.co.uk"
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
            })}
          />
          {errors.email && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{errors.email.message}</p>}
        </div>

        <div>
          <label className="form-label">Password</label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className={`form-input pr-10 ${errors.password ? 'error' : ''}`}
              placeholder="Your password"
              {...register('password', { required: 'Password is required' })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={18} />
            </button>
          </div>
          {errors.password && <p className="form-error"><Icon name="ExclamationCircleIcon" size={13} />{errors.password.message}</p>}
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-primary" {...register('rememberMe')} />
            <span className="text-sm text-foreground">Remember me</span>
          </label>
          <Link href="#" className="text-sm text-primary font-semibold hover:underline">Forgot password?</Link>
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3 text-base disabled:opacity-70 disabled:cursor-not-allowed"
          style={{ minHeight: 48 }}
        >
          {isLoading ? (
            <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
          ) : (
            <>
              <Icon name="ArrowRightOnRectangleIcon" size={18} />
              Sign In
            </>
          )}
        </button>
      </form>

      {/* Admin shortcut notice */}
      <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-3">
        <div className="flex items-start gap-2">
          <Icon name="ShieldCheckIcon" size={15} className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-800">
            <strong>Admin access:</strong> Sign in with your admin account, then visit{' '}
            <Link href="/admin" className="underline font-semibold">farmtradedirect.com/admin</Link>
          </p>
        </div>
      </div>

      <p className="text-center text-sm text-muted-foreground mt-6">
        Don&apos;t have an account?{' '}
        <button onClick={onSwitchToRegister} className="text-primary font-semibold hover:underline">
          Create one free
        </button>
      </p>
    </div>
  );
}